'use client';

import React from 'react';
import Link from 'next/link';
import Breadcrumb from './ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { AccessTimeIcon, WalletIcon, PersonOffIcon, PublicIcon, InfoIcon, VisibilityIcon, RocketLaunchIcon, VerifiedUserIcon, SpeedIcon, LockIcon, LanguageIcon, TipsIcon, SearchIcon, PaymentIcon, EmailIcon, ArrowForwardIcon, CheckCircleIcon, AutoAwesomeIcon, HandshakeIcon } from './icons';
import styles from './StaticPage.module.css';

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className={styles.offerCard}>
    <div className={styles.offerCardIcon}>{icon}</div>
    <h3 className={styles.offerCardTitle}>{title}</h3>
    <p className={styles.offerCardDesc}>{description}</p>
  </div>
);

const ValueItem = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className={styles.valueItemRow}>
    <span className={styles.valueItemIcon}>{icon}</span>
    <div>
      <p className={styles.valueItemTitle}>{title}</p>
      <p className={styles.valueItemDesc}>{description}</p>
    </div>
  </div>
);

const StepCard = ({ number, title, description, icon }: { number: string; title: string; description: string; icon: React.ReactNode }) => (
  <div className={styles.stepCard}>
    <div className={styles.stepNumber}>{number}</div>
    <div className={styles.stepIconCircle}>{icon}</div>
    <h3 className={styles.stepTitle}>{title}</h3>
    <p className={styles.stepDesc}>{description}</p>
  </div>
);

const AboutUs: React.FC = () => {
  const t = useTranslations('aboutus');
  const tCommon = useTranslations('common');

  const breadcrumbItems = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    { label: tCommon('breadcrumb.aboutUs'), path: '/about' },
  ];

  const brandCategories = [
    t('ourStory.categories.entertainment'),
    t('ourStory.categories.retail'),
    t('ourStory.categories.gaming'),
    t('ourStory.categories.travel'),
    t('ourStory.categories.streaming'),
    t('ourStory.categories.food'),
    t('ourStory.categories.lifestyle'),
  ];

  const whyAcebFeatures = [
    t('whyAceb.builtFor.features.delivery'),
    t('whyAceb.builtFor.features.payments'),
    t('whyAceb.builtFor.features.pricing'),
    t('whyAceb.builtFor.features.availability'),
    t('whyAceb.builtFor.features.dataCollection'),
    t('whyAceb.builtFor.features.support'),
  ];

  const trustedFeatures = [
    t('whyAceb.trusted.features.partnerships'),
    t('whyAceb.trusted.features.fulfillment'),
    t('whyAceb.trusted.features.encryption'),
    t('whyAceb.trusted.features.supportTeam'),
  ];

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.breadcrumbBar}>
        <div className={styles.container}><Breadcrumb items={breadcrumbItems} /></div>
      </div>

      <div className={styles.heroSection}>
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitlePlain}>{t('intro.title')}</h2>
          <p className={styles.bodyText}>{t('intro.paragraph1')}</p>
          <p className={styles.bodyText}>{t('intro.paragraph2')}</p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitlePlain}>{t('ourStory.title')}</h2>
          <h3 className={styles.subSectionTitleAbout}>{t('ourStory.subtitle')}</h3>
          <p className={styles.bodyText} style={{ fontStyle: 'italic' }}>{t('ourStory.mission')}</p>
          <p className={styles.bodyTextSm}>{t('ourStory.paragraph1')}</p>
          <p className={styles.bodyTextSm}>{t('ourStory.paragraph2')}</p>
          <div className={styles.chipRow}>
            {brandCategories.map((category, index) => (
              <span key={index} className={styles.chip}>{category}</span>
            ))}
          </div>
          <p className={styles.bodyTextSm}>{t('ourStory.paragraph3')}</p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitlePlain}>{t('whatWeOffer.title')}</h2>
          <div className={styles.offerGrid}>
            <FeatureCard icon={<AccessTimeIcon style={{ width: 28, height: 28 }} />} title={t('whatWeOffer.instantDelivery.title')} description={t('whatWeOffer.instantDelivery.description')} />
            <FeatureCard icon={<WalletIcon style={{ width: 28, height: 28 }} />} title={t('whatWeOffer.wideCryptoSupport.title')} description={t('whatWeOffer.wideCryptoSupport.description')} />
            <FeatureCard icon={<PersonOffIcon style={{ width: 28, height: 28 }} />} title={t('whatWeOffer.noAccountRequired.title')} description={t('whatWeOffer.noAccountRequired.description')} />
            <FeatureCard icon={<PublicIcon style={{ width: 28, height: 28 }} />} title={t('whatWeOffer.globalReach.title')} description={t('whatWeOffer.globalReach.description')} />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitlePlain}>{t('whyAceb.title')}</h2>
          <div className={styles.whyGrid}>
            <div className={styles.whyCard}>
              <div className={styles.whyCardIcon}><AutoAwesomeIcon style={{ width: 26, height: 26 }} /></div>
              <h3 className={styles.whyCardTitle}>{t('whyAceb.realUtility.title')}</h3>
              <p className={styles.whyCardText}>{t('whyAceb.realUtility.paragraph1')}</p>
              <p className={styles.whyCardText}>{t('whyAceb.realUtility.paragraph2')}</p>
            </div>
            <div className={styles.whyCard}>
              <div className={styles.whyCardIcon}><SpeedIcon /></div>
              <h3 className={styles.whyCardTitle}>{t('whyAceb.builtFor.title')}</h3>
              <p className={styles.whyCardText}>{t('whyAceb.builtFor.intro')}</p>
              <div style={{ marginBottom: 16 }}>
                {whyAcebFeatures.map((feature, index) => (
                  <div key={index} className={styles.featureCheckItem}>
                    <CheckCircleIcon style={{ width: 18, height: 18 }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <p className={styles.whyCardTextItalic}>{t('whyAceb.builtFor.outro')}</p>
            </div>
            <div className={styles.whyCard}>
              <div className={styles.whyCardIcon}><HandshakeIcon style={{ width: 26, height: 26 }} /></div>
              <h3 className={styles.whyCardTitle}>{t('whyAceb.trusted.title')}</h3>
              <p className={styles.whyCardText}>{t('whyAceb.trusted.paragraph1')} {t('whyAceb.trusted.paragraph2')}</p>
              <div style={{ marginBottom: 16 }}>
                {trustedFeatures.map((feature, index) => (
                  <div key={index} className={styles.featureCheckItem}>
                    <CheckCircleIcon style={{ width: 18, height: 18 }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <p className={styles.whyCardTextItalic}>{t('whyAceb.trusted.outro')}</p>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitlePlain}>{t('missionVisionValues.title')}</h2>
          <div className={styles.mvvGrid}>
            <div className={styles.mvvCardMission}>
              <div className={styles.mvvHeader}>
                <span style={{ color: 'var(--color-primary)' }}><RocketLaunchIcon /></span>
                <h3 className={styles.mvvTitle}>{t('missionVisionValues.mission.title')}</h3>
              </div>
              <p className={styles.mvvDesc}>{t('missionVisionValues.mission.description')}</p>
            </div>
            <div className={styles.mvvCardVision}>
              <div className={styles.mvvHeader}>
                <span style={{ color: 'var(--color-danger)' }}><VisibilityIcon /></span>
                <h3 className={styles.mvvTitle}>{t('missionVisionValues.vision.title')}</h3>
              </div>
              <p className={styles.mvvDesc}>{t('missionVisionValues.vision.description')}</p>
            </div>
            <div className={styles.mvvCardValues}>
              <div className={styles.mvvHeader}>
                <span style={{ color: 'var(--color-primary)' }}><VerifiedUserIcon /></span>
                <h3 className={styles.mvvTitle}>{t('missionVisionValues.values.title')}</h3>
              </div>
              <ValueItem icon={<SpeedIcon />} title={t('missionVisionValues.values.speed.title')} description={t('missionVisionValues.values.speed.description')} />
              <ValueItem icon={<LockIcon />} title={t('missionVisionValues.values.security.title')} description={t('missionVisionValues.values.security.description')} />
              <ValueItem icon={<LanguageIcon />} title={t('missionVisionValues.values.accessibility.title')} description={t('missionVisionValues.values.accessibility.description')} />
              <ValueItem icon={<VisibilityIcon />} title={t('missionVisionValues.values.transparency.title')} description={t('missionVisionValues.values.transparency.description')} />
              <ValueItem icon={<TipsIcon />} title={t('missionVisionValues.values.innovation.title')} description={t('missionVisionValues.values.innovation.description')} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitleCentered}>{t('howItWorks.title')}</h2>
          <div className={styles.stepsGrid}>
            <StepCard number="1" icon={<SearchIcon style={{ width: 36, height: 36 }} />} title={t('howItWorks.step1.title')} description={t('howItWorks.step1.description')} />
            <StepCard number="2" icon={<PaymentIcon style={{ width: 36, height: 36 }} />} title={t('howItWorks.step2.title')} description={t('howItWorks.step2.description')} />
            <StepCard number="3" icon={<EmailIcon style={{ width: 36, height: 36 }} />} title={t('howItWorks.step3.title')} description={t('howItWorks.step3.description')} />
          </div>
        </div>

        <div className={styles.ctaSection}>
          <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
          <p className={styles.ctaText}>{t('cta.description')}</p>
          <div className={styles.ctaButtons}>
            <Link href="/giftcards" className={styles.ctaButton}>
              {t('cta.exploreButton')} <ArrowForwardIcon fontSize="small" />
            </Link>
            <Link href="/faq" className={styles.ctaButtonOutline}>
              {t('cta.faqButton')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;